import { useState, useEffect } from "react";
import { adminGetDisbursements, adminApproveDisbursement, adminTransferDisbursement, adminCreateDisbursement, adminGetApplications } from "@/api";
import { toast } from "sonner";
import { Banknote, Search, CheckCircle, Loader2, ArrowRight, X, Plus, IndianRupee, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/config/navigationConfig";

export default function AdminDisbursements() {
  const { user } = useAuth();
  const canApprove = canWrite(user, 'approve_disbursements');
  const [disbursements, setDisbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [utrModal, setUtrModal] = useState(null);
  const [utrNumber, setUtrNumber] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [approvedApps, setApprovedApps] = useState([]);
  const [createForm, setCreateForm] = useState({ application_id: '', bank_account: '', ifsc_code: '', amount: '' });
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => { loadDisbursements(); }, [filter]);

  const loadDisbursements = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const res = await adminGetDisbursements(params);
      setDisbursements(res.data.disbursements || []);
    } catch (err) {
      toast.error("Failed to load disbursements");
    } finally { setLoading(false); }
  };

  const loadApprovedApps = async () => {
    try {
      const res = await adminGetApplications({ status: 'approved' });
      setApprovedApps(res.data.applications || []);
    } catch (err) {
      toast.error("Failed to load applications");
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await adminApproveDisbursement(id);
      toast.success("Disbursement approved");
      loadDisbursements();
    } catch (err) {
      toast.error("Failed to approve");
    } finally { setActionLoading(null); }
  };

  const handleTransfer = async () => {
    if (!utrNumber.trim()) { toast.error("Enter UTR number"); return; }
    setActionLoading(utrModal);
    try {
      await adminTransferDisbursement(utrModal, utrNumber);
      toast.success("Transfer confirmed!");
      setUtrModal(null);
      setUtrNumber('');
      loadDisbursements();
    } catch (err) {
      toast.error("Transfer failed");
    } finally { setActionLoading(null); }
  };

  const handleCreate = async () => {
    if (!createForm.application_id || !createForm.bank_account || !createForm.ifsc_code || !createForm.amount) {
      toast.error("Please fill all fields");
      return;
    }
    setCreateLoading(true);
    try {
      await adminCreateDisbursement({
        application_id: createForm.application_id,
        bank_account: createForm.bank_account,
        ifsc_code: createForm.ifsc_code,
        amount: parseFloat(createForm.amount),
      });
      toast.success("Disbursement created with repayment schedule!");
      setShowCreate(false);
      setCreateForm({ application_id: '', bank_account: '', ifsc_code: '', amount: '' });
      loadDisbursements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create disbursement");
    } finally { setCreateLoading(false); }
  };

  const openCreateModal = () => {
    loadApprovedApps();
    setShowCreate(true);
  };

  const selectApp = (app) => {
    setCreateForm({
      ...createForm,
      application_id: app.id,
      amount: String(app.amount_requested || app.loan_amount || ''),
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800",
      approved: "bg-green-100 text-green-800",
      transferred: "bg-blue-100 text-blue-800",
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const filteredDisbs = disbursements.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (d.user?.name || '').toLowerCase().includes(s) || (d.utr_number || '').toLowerCase().includes(s) || (d.application?.application_number || '').toLowerCase().includes(s);
  });

  return (
    <div className="fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Disbursements</h1>
          <p className="text-sm text-gray-500 mt-1">Manage fund disbursements and transfers</p>
        </div>
        {canApprove ? (
          <Button onClick={openCreateModal} className="bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-xl" data-testid="btn-new-disbursement">
            <Plus className="h-4 w-4 mr-2" /> New Disbursement
          </Button>
        ) : (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-900" title="Requires approve_disbursements permission" data-testid="disb-no-perm-badge">
            <Lock className="h-3.5 w-3.5" /> View-only access
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm" />
        </div>
        <div className="flex gap-2">
          {['', 'pending', 'approved', 'transferred'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${filter === f ? 'bg-[#1565C0] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {f || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#1565C0]" /></div>
      ) : filteredDisbs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <Banknote className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No disbursements found</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">App #</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Account</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">UTR</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredDisbs.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs">{d.application?.application_number || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-sm">{d.user?.name || 'N/A'}</div>
                      <div className="text-xs text-gray-400">{d.user?.mobile || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{d.bank_account || 'N/A'}</div>
                      <div className="text-gray-400">{d.ifsc || ''}</div>
                    </td>
                    <td className="px-4 py-3 font-bold">₹{parseFloat(d.amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-xs">{d.utr_number || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(d.status)}`}>
                        {d.status?.charAt(0).toUpperCase() + d.status?.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {d.status === 'pending' && canApprove && (
                          <Button onClick={() => handleApprove(d.id)} disabled={actionLoading === d.id} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs" data-testid={`approve-disb-${d.id}`}>
                            {actionLoading === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle className="h-3 w-3 mr-1" />Approve</>}
                          </Button>
                        )}
                        {d.status === 'approved' && canApprove && (
                          <Button onClick={() => setUtrModal(d.id)} size="sm" className="bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-lg text-xs" data-testid={`transfer-disb-${d.id}`}>
                            <ArrowRight className="h-3 w-3 mr-1" /> Transfer
                          </Button>
                        )}
                        {!canApprove && (d.status === 'pending' || d.status === 'approved') && (
                          <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            <Lock className="h-2.5 w-2.5 inline mr-1" /> view-only
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* UTR Modal */}
      {utrModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-[#0A2540]">Confirm Transfer</h3>
              <button onClick={() => setUtrModal(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold">UTR / Transaction Number</Label>
            <Input value={utrNumber} onChange={e => setUtrNumber(e.target.value)} placeholder="Enter UTR number..." className="mt-2 rounded-xl" />
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setUtrModal(null)} variant="outline" className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={handleTransfer} disabled={actionLoading === utrModal} className="flex-1 bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-xl">
                {actionLoading === utrModal ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Transfer"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Disbursement Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-lg text-[#0A2540]">Create Disbursement</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            {/* Select Approved Application */}
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2 block">Select Approved Application</Label>
              {approvedApps.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4">No approved applications available for disbursement.</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {approvedApps.map(app => (
                    <button key={app.id} onClick={() => selectApp(app)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${createForm.application_id === app.id ? 'border-[#1565C0] bg-[#F0F4FF]' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-mono text-xs text-gray-400">{app.application_number || 'N/A'}</span>
                          <div className="font-semibold text-sm">{app.user?.name || 'Unknown'}</div>
                        </div>
                        <span className="font-bold text-[#1565C0]">₹{parseFloat(app.amount_requested || app.loan_amount || 0).toLocaleString()}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2 block">Bank Account Number</Label>
                <Input value={createForm.bank_account} onChange={e => setCreateForm({...createForm, bank_account: e.target.value})} placeholder="Account number" className="rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2 block">IFSC Code</Label>
                <Input value={createForm.ifsc_code} onChange={e => setCreateForm({...createForm, ifsc_code: e.target.value})} placeholder="e.g. HDFC0001234" className="rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2 block">Disbursement Amount</Label>
                <Input type="number" value={createForm.amount} onChange={e => setCreateForm({...createForm, amount: e.target.value})} placeholder="Amount" className="rounded-xl" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={handleCreate} disabled={createLoading} className="flex-1 bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-xl">
                {createLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Disbursement"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
